import type { ServiceCode } from '@common/enums/serviceCode';
import type { ServiceType } from '@common/enums/serviceType';
import type { Uuid } from '@common/utils/uuid';
import { ValidationErrorCode } from '@common/enums/validationErrorCode';
import { fetchJson } from '@frontend/store/fetch';

export type FundingServicePart = {
    fundingForActivityCodes: string[];
    id: Uuid;
    invoiceServiceCode: ServiceCode | null;
    invoiceServiceName: string | null;
    invoiceServiceType: ServiceType | null;
    name: string;
};

export type NewFundingServicePart = Omit<FundingServicePart, 'id'>;

const baseUrl = '/api/fundingServiceParts';

export async function getFundingServiceParts(serviceId: Uuid, signal: AbortSignal): Promise<FundingServicePart[]> {
    const url = `${baseUrl}/serviceId/${serviceId}`;
    const result = await fetchJson({ method: 'GET', signal, url });

    return result?.json.items;
}

export async function createFundingServicePart(serviceId: Uuid, part: NewFundingServicePart): Promise<void> {
    await fetchJson({
        body: { ...part, serviceId },
        method: 'POST',
        url: baseUrl,
    });
}

export async function updateFundingServicePart(serviceId: Uuid, part: FundingServicePart): Promise<void> {
    const { id, ...body } = part;
    const url = `${baseUrl}/${id}`;
    await fetchJson({
        body,
        method: 'POST',
        url,
    });
}

export async function removeFundingServicePart(
    id: Uuid
): Promise<ValidationErrorCode.CANNOT_DELETE_RESOURCE_IN_USE | undefined> {
    const url = `${baseUrl}/${id}`;
    const result = await fetchJson({
        allowedErrorCodes: [ValidationErrorCode.CANNOT_DELETE_RESOURCE_IN_USE],
        method: 'DELETE',
        url,
    });
    if (result.errorCode && result.errorCode === ValidationErrorCode.CANNOT_DELETE_RESOURCE_IN_USE) {
        return ValidationErrorCode.CANNOT_DELETE_RESOURCE_IN_USE;
    }
}
